import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { getLeads, createLead, updateLead, deleteLead, importLeadsCSV, getOpportunities, createOpportunity, updateOpportunity, getQuotations, createQuotation, convertQuotationToInvoice, getQuotationPDF } from '../lib/api';
import { Plus, Search, Filter, MoreVertical, Phone, Mail, Building, TrendingUp, FileText, ArrowRight, Upload, FileUp, Download } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const LEAD_STATUSES = ['new', 'contacted', 'qualified', 'unqualified'];
const LEAD_SOURCES = ['manual', 'website', 'referral', 'social', 'event', 'cold_call'];
const OPP_STAGES = ['qualification', 'proposal', 'negotiation', 'closed_won', 'closed_lost'];

const statusColors = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-amber-100 text-amber-700',
  qualified: 'bg-emerald-100 text-emerald-700',
  unqualified: 'bg-slate-100 text-slate-700',
  qualification: 'bg-blue-100 text-blue-700',
  proposal: 'bg-amber-100 text-amber-700',
  negotiation: 'bg-purple-100 text-purple-700',
  closed_won: 'bg-emerald-100 text-emerald-700',
  closed_lost: 'bg-rose-100 text-rose-700',
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  converted: 'bg-amber-100 text-amber-700'
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 0 }).format(amount);
};

// Lead Form Component
const LeadForm = ({ lead, onSave, onClose }) => {
  const [form, setForm] = useState(lead || {
    name: '', email: '', phone: '', company: '', source: 'manual', score: 0, status: 'new', notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (lead?.id) {
        await updateLead(lead.id, form);
        toast.success('Lead updated');
      } else {
        await createLead(form);
        toast.success('Lead created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to save lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Name *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="lead-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Company</Label>
          <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Phone</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Source</Label>
          <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {LEAD_SOURCES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {LEAD_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Score (0-100)</Label>
          <Input type="number" min="0" max="100" value={form.score} onChange={(e) => setForm({ ...form, score: parseInt(e.target.value) || 0 })} />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="save-lead-btn">{saving ? 'Saving...' : 'Save Lead'}</Button>
      </div>
    </form>
  );
};

// Opportunity Form Component
const OpportunityForm = ({ opportunity, onSave, onClose }) => {
  const [form, setForm] = useState(opportunity || {
    name: '', stage: 'qualification', value: 0, probability: 10, expected_close_date: '', notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (opportunity?.id) {
        await updateOpportunity(opportunity.id, form);
        toast.success('Opportunity updated');
      } else {
        await createOpportunity(form);
        toast.success('Opportunity created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to save opportunity');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Opportunity Name *</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="opp-name-input" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Stage</Label>
          <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {OPP_STAGES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Probability (%)</Label>
          <Input type="number" min="0" max="100" value={form.probability} onChange={(e) => setForm({ ...form, probability: parseInt(e.target.value) || 0 })} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Value (ZAR)</Label>
          <Input type="number" min="0" value={form.value} onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value) || 0 })} />
        </div>
        <div className="space-y-2">
          <Label>Expected Close Date</Label>
          <Input type="date" value={form.expected_close_date?.split('T')[0] || ''} onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })} />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="save-opp-btn">{saving ? 'Saving...' : 'Save Opportunity'}</Button>
      </div>
    </form>
  );
};

// Quotation Form Component
const QuotationForm = ({ quotation, onSave, onClose }) => {
  const [form, setForm] = useState(quotation || {
    client_name: '', client_email: '', items: [{ description: '', quantity: 1, unit_price: 0 }],
    subtotal: 0, vat_rate: 15, vat_amount: 0, total: 0, currency: 'ZAR', status: 'draft', notes: ''
  });
  const [saving, setSaving] = useState(false);

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const vatAmount = subtotal * (form.vat_rate / 100);
    return { subtotal, vat_amount: vatAmount, total: subtotal + vatAmount };
  };

  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], [field]: value };
    const totals = calculateTotals(newItems);
    setForm({ ...form, items: newItems, ...totals });
  };

  const addItem = () => {
    setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit_price: 0 }] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createQuotation(form);
      toast.success('Quotation created');
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to create quotation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Client Name *</Label>
          <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required data-testid="quote-client-input" />
        </div>
        <div className="space-y-2">
          <Label>Client Email</Label>
          <Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Line Items</Label>
        <div className="border rounded-lg divide-y">
          {form.items.map((item, idx) => (
            <div key={idx} className="p-3 grid grid-cols-4 gap-2 items-end">
              <div className="col-span-2">
                <Input placeholder="Description" value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} />
              </div>
              <Input type="number" placeholder="Qty" min="1" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)} />
              <Input type="number" placeholder="Unit Price" min="0" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} />
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addItem}>+ Add Item</Button>
      </div>

      <div className="grid grid-cols-3 gap-4 text-right pt-4 border-t">
        <div></div>
        <div className="text-sm text-muted-foreground">Subtotal:</div>
        <div className="font-mono font-semibold">{formatCurrency(form.subtotal)}</div>
        <div></div>
        <div className="text-sm text-muted-foreground">VAT ({form.vat_rate}%):</div>
        <div className="font-mono">{formatCurrency(form.vat_amount)}</div>
        <div></div>
        <div className="font-semibold">Total:</div>
        <div className="font-mono font-bold text-lg">{formatCurrency(form.total)}</div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="save-quote-btn">{saving ? 'Saving...' : 'Create Quotation'}</Button>
      </div>
    </form>
  );
};

// Main Sales Page Component
export const SalesPage = () => {
  const [leads, setLeads] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [leadDialogOpen, setLeadDialogOpen] = useState(false);
  const [oppDialogOpen, setOppDialogOpen] = useState(false);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [activeTab, setActiveTab] = useState("leads");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [leadsRes, oppsRes, quotesRes] = await Promise.all([
        getLeads(), getOpportunities(), getQuotations()
      ]);
      setLeads(leadsRes.data);
      setOpportunities(oppsRes.data);
      setQuotations(quotesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLead = async (id) => {
    try {
      await deleteLead(id);
      toast.success('Lead deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleConvertToInvoice = async (quoteId) => {
    try {
      await convertQuotationToInvoice(quoteId);
      toast.success('Quotation converted to invoice');
      loadData();
    } catch (error) {
      toast.error('Failed to convert quotation');
    }
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    try {
      const text = await file.text();
      const response = await importLeadsCSV(text);
      const { imported_count, failed_count, errors } = response.data;
      
      if (imported_count > 0) {
        toast.success(`Successfully imported ${imported_count} leads`);
      }
      if (failed_count > 0) {
        toast.warning(`${failed_count} rows failed to import`);
        if (errors?.length > 0) {
          console.log('Import errors:', errors);
        }
      }
      
      loadData();
      setImportDialogOpen(false);
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import CSV file');
    } finally {
      setImporting(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const filteredLeads = leads.filter(l => 
    l.name.toLowerCase().includes(search.toLowerCase()) ||
    l.company?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredOpps = opportunities.filter(o => 
    o.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="sales-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Sales & CRM</h1>
          <p className="text-muted-foreground mt-1">Manage leads, opportunities, and quotations</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Search..." 
              className="pl-9 w-64" 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="sales-search"
            />
          </div>
        </div>
      </div>

      <Tabs defaultValue="leads" className="space-y-6">
        <TabsList>
          <TabsTrigger value="leads" data-testid="leads-tab">Leads ({leads.length})</TabsTrigger>
          <TabsTrigger value="opportunities" data-testid="opportunities-tab">Opportunities ({opportunities.length})</TabsTrigger>
          <TabsTrigger value="quotations" data-testid="quotations-tab">Quotations ({quotations.length})</TabsTrigger>
        </TabsList>

        {/* Leads Tab */}
        <TabsContent value="leads" className="space-y-4">
          <div className="flex justify-end gap-2">
            {/* CSV Import Dialog */}
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="import-leads-btn">
                  <Upload className="h-4 w-4 mr-2" /> Import CSV
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Import Leads from CSV</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Upload a CSV file containing leads data. The file should include columns like: Name, Email, Phone, Company, Status, Source, etc.
                  </p>
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                    <FileUp className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <label className="cursor-pointer">
                      <span className="text-primary font-medium hover:underline">
                        {importing ? 'Importing...' : 'Choose CSV file'}
                      </span>
                      <input
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={handleCSVImport}
                        disabled={importing}
                        data-testid="csv-file-input"
                      />
                    </label>
                    <p className="text-xs text-muted-foreground mt-2">
                      Supported columns: Name, Email, Phone, Company, Status, Source, Description, Country, City, Tags
                    </p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Lead Dialog */}
            <Dialog open={leadDialogOpen} onOpenChange={setLeadDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setSelectedLead(null)} data-testid="add-lead-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Lead
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{selectedLead ? 'Edit Lead' : 'New Lead'}</DialogTitle>
                </DialogHeader>
                <LeadForm lead={selectedLead} onSave={loadData} onClose={() => setLeadDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.map((lead) => (
                  <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`}>
                    <TableCell className="font-medium">{lead.name}</TableCell>
                    <TableCell>{lead.company || '-'}</TableCell>
                    <TableCell className="text-sm">
                      {lead.email ? (
                        <a href={`mailto:${lead.email}`} className="text-primary hover:underline flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          <span className="truncate max-w-[180px]">{lead.email}</span>
                        </a>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {lead.phone ? (
                        <a href={`tel:${lead.phone}`} className="text-muted-foreground hover:text-primary flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          <span>{lead.phone}</span>
                        </a>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="capitalize text-sm">{lead.source.replace('_', ' ')}</TableCell>
                    <TableCell><Badge variant="outline" className="font-mono">{lead.score}</Badge></TableCell>
                    <TableCell><Badge className={statusColors[lead.status]}>{lead.status}</Badge></TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm"><MoreVertical className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { setSelectedLead(lead); setLeadDialogOpen(true); }}>Edit</DropdownMenuItem>
                          <DropdownMenuItem className="text-rose-600" onClick={() => handleDeleteLead(lead.id)}>Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredLeads.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No leads found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Opportunities Tab - Kanban Style */}
        <TabsContent value="opportunities" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={oppDialogOpen} onOpenChange={setOppDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setSelectedOpp(null)} data-testid="add-opp-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Opportunity
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{selectedOpp ? 'Edit Opportunity' : 'New Opportunity'}</DialogTitle>
                </DialogHeader>
                <OpportunityForm opportunity={selectedOpp} onSave={loadData} onClose={() => setOppDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-5 gap-4">
            {OPP_STAGES.map((stage) => (
              <div key={stage} className="kanban-column">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold capitalize text-sm">{stage.replace('_', ' ')}</h3>
                  <Badge variant="outline" className="font-mono">
                    {formatCurrency(opportunities.filter(o => o.stage === stage).reduce((s, o) => s + o.value, 0))}
                  </Badge>
                </div>
                <div className="space-y-3">
                  {opportunities.filter(o => o.stage === stage).map((opp) => (
                    <Card key={opp.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setSelectedOpp(opp); setOppDialogOpen(true); }}>
                      <CardContent className="p-4 space-y-2">
                        <p className="font-medium text-sm">{opp.name}</p>
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-sm font-semibold text-amber-600">{formatCurrency(opp.value)}</span>
                          <Badge variant="outline" className="text-xs">{opp.probability}%</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Quotations Tab */}
        <TabsContent value="quotations" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-quote-btn">
                  <Plus className="h-4 w-4 mr-2" /> New Quotation
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>Create Quotation</DialogTitle>
                </DialogHeader>
                <QuotationForm onSave={loadData} onClose={() => setQuoteDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quote #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotations.map((quote) => (
                  <TableRow key={quote.id}>
                    <TableCell className="font-mono text-sm">{quote.quote_number}</TableCell>
                    <TableCell className="font-medium">{quote.client_name}</TableCell>
                    <TableCell>{quote.items?.length || 0} items</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{formatCurrency(quote.total)}</TableCell>
                    <TableCell><Badge className={statusColors[quote.status]}>{quote.status}</Badge></TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm"><MoreVertical className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={async () => {
                            try {
                              const response = await getQuotationPDF(quote.id);
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', `quote_${quote.quote_number}.pdf`);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                              toast.success('Quotation PDF downloaded');
                            } catch (error) {
                              toast.error('Failed to download PDF');
                            }
                          }}>
                            <Download className="h-4 w-4 mr-2" /> Download PDF
                          </DropdownMenuItem>
                          {quote.status !== 'converted' && (
                            <DropdownMenuItem onClick={() => handleConvertToInvoice(quote.id)}>
                              <ArrowRight className="h-4 w-4 mr-2" /> Convert to Invoice
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {quotations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No quotations found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
